import React, { useEffect, useState } from "react";
import { Card, CardBody, Col } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { CustomerService } from "./service";
import { ErrorToast, SuccessToast } from "../../shared/toasters/toasters";
import DeleteModal from "../../Common/DeleteModal";
import Select from "react-select";
import { useTranslation } from "react-i18next";
import { usePagination } from "../../shared/hooks/usePagination";
import {
  ICustomer,
  ICustomerImportResult,
} from "../../shared/interfaces/customer/ICustomer";
import { CustomPagination } from "../../shared/components/CustomPagination";
import CustomerImportResultModal from "./CustomerImportResultModal";
const PER_PAGE_DEFAULT = 10;
export const entriesPerPageOptions = [
  { label: "10", value: 10 },
  { label: "25", value: 25 },
  { label: "50", value: 50 },
  { label: "100", value: 100 },
  { label: "200", value: 200 },
];

const CustomerList = () => {
  const { t } = useTranslation();
  const [list, setList] = useState<ICustomer[] | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [customerToDelete, setCustomerToDelete] = useState<number | null>(null);
  const [uploading, setUploading] = useState(false);
  const [importResult, setImportResult] = useState<ICustomerImportResult | null>(
    null,
  );
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const {
    page,
    handlePages,
    pagination,
    totalPages,
    setTotalPages,
    setCount,
    changeFilterHandler,
    goToPreviousPage,
    goToNextPage,
  } = usePagination({
    perPage: PER_PAGE_DEFAULT,
  });

  const handleDeleteClick = (id: number) => {
    setCustomerToDelete(id);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (customerToDelete !== null) {
      try {
        await CustomerService.delete(customerToDelete);
        SuccessToast(t("customer.successDelete"));
        setShowDeleteModal(false);
        setCustomerToDelete(null);
        fetchData();
      } catch (error) {
        ErrorToast(error);
      }
    }
  };

  const fetchData = async () => {
    try {
      const response = await CustomerService.get(pagination);
      const data = response.data;
      setList(data);
      setTotalPages(response.totalPages);
    } catch (error) {
      ErrorToast(error);
    }
  };

  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const allowedTypes = [
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "text/csv",
    ];

    if (!allowedTypes.includes(file.type)) {
      ErrorToast(t("customer.invalidFileType"));
      return;
    }

    const formData = new FormData();
    formData.append("file", file);

    setUploading(true);
    try {
      const response = await CustomerService.upload(formData);
      setImportResult(response);
      if (response?.created?.length) {
        SuccessToast(t("customer.importSuccess"));
      }
      fetchData();
    } catch (error) {
      ErrorToast(error);
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  useEffect(() => {
    fetchData();
  }, [pagination]);

  return (
    <React.Fragment>
      <Col>
        <Card>
          <CardBody>
            <div className="text-end">
              <input
                type="file"
                ref={fileInputRef}
                style={{ display: "none" }}
                accept=".xlsx,.xls,.csv"
                onChange={handleFileUpload}
              />
              <button
                className="btn btn-success text-end me-2"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
              >
                <i className="ti ti-upload f-18"></i>{" "}
                {uploading ? t("general.uploading") : t("customer.import")}
              </button>
              <button
                className="btn btn-primary text-end"
                onClick={() => navigate("/customers/create")}
              >
                <i className="ti ti-plus f-18"></i> {t("customer.add")}
              </button>
            </div>

            <div className="d-sm-flex align-items-center mb-3">
              <ul className="list-inline me-auto my-1">
                <li className="list-inline-item">
                  <Select
                    placeholder={t("customer.entriesPerPage")}
                    options={entriesPerPageOptions}
                    onChange={(selected) =>
                      changeFilterHandler(
                        "perPage",
                        selected?.value ?? PER_PAGE_DEFAULT,
                      )
                    }
                    defaultValue={entriesPerPageOptions.find(
                      (option) => option.value === PER_PAGE_DEFAULT,
                    )}
                    classNamePrefix="react-select"
                  />
                </li>
                <span> {t("customer.entriesPerPage")}</span>
              </ul>
              <ul className="list-inline ms-auto my-1 mt-3">
                <li className="list-inline-item">
                  <form className="">
                    <input
                      className="form-control"
                      type="text"
                      placeholder={t("customer.searchPlaceholder")}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        changeFilterHandler("term", e.target.value)
                      }
                    />
                  </form>
                </li>
              </ul>
            </div>
            <div className="table-responsive">
              <table className="table table-hover" id="pc-dt-simple-1">
                <thead>
                  <tr>
                    <th>{t("customer.id")}</th>
                    <th>{t("customer.name")}</th>
                    <th>{t("customer.email")}</th>
                    <th>{t("customer.phone")}</th>

                    <th className="text-end">{t("customer.actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {list?.map((customer: any) => (
                    <tr key={customer?.id}>
                      <td>{customer?.id}</td>
                      <td>
                        {customer?.name} {customer?.lastName}
                      </td>
                      <td>{customer?.email}</td>
                      <td>{customer?.phone}</td>

                      <td className="text-end">
                        <ul className="list-inline mb-0">
                          <li
                            className="list-inline-item"
                            onClick={() =>
                              navigate(`/customers/edit/${customer.id}`)
                            }
                          >
                            <a
                              href="#"
                              className="avtar avtar-s btn-link-success btn-pc-default"
                            >
                              <i className="ti ti-edit f-20"></i>
                            </a>
                          </li>
                          <li className="list-inline-item">
                            <span
                              role="button"
                              className="avtar avtar-s btn-link-danger btn-pc-default"
                              onClick={() => handleDeleteClick(customer.id)}
                            >
                              <i className="ti ti-trash f-20"></i>
                            </span>
                          </li>
                        </ul>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <CustomPagination
              page={page}
              totalPages={totalPages}
              handlePages={handlePages}
              goToPreviousPage={goToPreviousPage}
              goToNextPage={goToNextPage}
            />
          </CardBody>
        </Card>
      </Col>

      <CustomerImportResultModal
        show={!!importResult}
        handleClose={() => setImportResult(null)}
        result={importResult}
      />

      <DeleteModal
        show={showDeleteModal}
        handleClose={() => setShowDeleteModal(false)}
        handleDeleteId={handleDeleteConfirm}
        name={t("customer.name")}
      />
    </React.Fragment>
  );
};

export default CustomerList;
